import React, { useState } from 'react';

const EMPTY_PROJECT = {
    title: '',
    description: '',
    scopeItems: [''],
    scopeComment: '',
    timeExpectation: '',
    materialsMode: 'single',
    materials: [''],
    materialGroups: [{ title: '', comment: '', cost: '', url: '' }],
    materialsComment: '',
    laborPrice: '',
    materialsEstimate: '',
    totalPrice: '',
    pricingNote: '',
    optionCards: [],
    optionCardsComment: '',
};

function NewQuote() {
    const [password, setPassword] = useState('');
    const [customPassword, setCustomPassword] = useState('');
    const [authed, setAuthed] = useState(false);
    const [authError, setAuthError] = useState('');

    const [clientName, setClientName] = useState('');
    const [clientPhone, setClientPhone] = useState('');
    const [clientPhone2, setClientPhone2] = useState('');
    const DEFAULT_INTRO = `<p>Thank you again for the opportunity to review your project. I've gone through the details and prepared the information below.</p>
<p>As promised, you'll find a breakdown of the quoted work. I've included notes where helpful to provide additional context around certain items.</p>
<p>The scope of work is outlined below. If the project includes multiple components, they are presented separately so you can move forward with any portion at your discretion. When you're ready to proceed, I can provide a formal quote for approval.</p>`;

    const [introMessage, setIntroMessage] = useState(DEFAULT_INTRO);
    const [projects, setProjects] = useState([{ ...EMPTY_PROJECT }]);

    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    function handlePasswordSubmit(e) {
        e.preventDefault();
        if (password.trim() === '') {
            setAuthError('Enter a password.');
            return;
        }
        setAuthed(true);
    }

    function updateProject(index, field, value) {
        setProjects(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    }

    function updateListItem(projectIndex, field, itemIndex, value) {
        setProjects(prev => {
            const updated = [...prev];
            const list = [...updated[projectIndex][field]];
            list[itemIndex] = value;
            updated[projectIndex] = { ...updated[projectIndex], [field]: list };
            return updated;
        });
    }

    function addListItem(projectIndex, field) {
        setProjects(prev => {
            const updated = [...prev];
            updated[projectIndex] = {
                ...updated[projectIndex],
                [field]: [...updated[projectIndex][field], ''],
            };
            return updated;
        });
    }

    function removeListItem(projectIndex, field, itemIndex) {
        setProjects(prev => {
            const updated = [...prev];
            const list = updated[projectIndex][field].filter((_, i) => i !== itemIndex);
            updated[projectIndex] = { ...updated[projectIndex], [field]: list };
            return updated;
        });
    }

    function addProject() {
        setProjects(prev => [...prev, { ...EMPTY_PROJECT, scopeItems: [''], materials: [''] }]);
    }

    function removeProject(index) {
        setProjects(prev => prev.filter((_, i) => i !== index));
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setSubmitting(true);
        setError('');
        try {
            const res = await fetch('/api/quoteRoute/create', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ password, clientName, clientPhone, clientPhone2, customPassword, introMessage, projects }),
            });
            const data = await res.json();
            if (!res.ok) {
                setError(data.error || 'Something went wrong.');
            } else {
                setResult(data);
            }
        } catch (err) {
            setError('Network error.');
        } finally {
            setSubmitting(false);
        }
    }

    if (!authed) {
        return (
            <div style={styles.gate}>
                <div style={styles.gateBox}>
                    <h2 style={styles.gateTitle}>New Quote</h2>
                    <p style={styles.gateSubtitle}>This page is for employees only.</p>
                    <form onSubmit={handlePasswordSubmit}>
                        <input
                            type="password"
                            placeholder="Password"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            style={styles.input}
                        />
                        {authError && <p style={styles.errorText}>{authError}</p>}
                        <button type="submit" style={styles.button}>Enter</button>
                    </form>
                </div>
            </div>
        );
    }

    if (result) {
        const fullUrl = `${window.location.origin}/${result.id}`;
        return (
            <div style={styles.gate}>
                <div style={styles.gateBox}>
                    <h2 style={styles.gateTitle}>Quote created!</h2>
                    <p>Share this link with the client:</p>
                    <a href={fullUrl} style={styles.link}>{fullUrl}</a>
                    <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#6b7280' }}>
                        They'll need to enter their phone number to view it.
                    </p>
                    <button style={{ ...styles.button, marginTop: '1.5rem' }} onClick={() => {
                        setResult(null);
                        setClientName('');
                        setClientPhone('');
                        setIntroMessage('');
                        setCustomPassword('');
                        setClientPhone2('');
                        setProjects([{ ...EMPTY_PROJECT, scopeItems: [''], materials: [''] }]);
                    }}>
                        Create another
                    </button>
                    <button style={{ ...styles.button, marginTop: '1.5rem' }} onClick={() => {
                        setResult(null);
                    }}>
                        Create similar
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.page}>
            <h1 style={styles.pageTitle}>New Quote</h1>
            <form onSubmit={handleSubmit} style={styles.form}>

                <section style={styles.section}>
                    <h2 style={styles.sectionTitle}>Client Info</h2>
                    <label style={styles.label}>Client name</label>
                    <input
                        style={styles.input}
                        value={clientName}
                        onChange={e => setClientName(e.target.value)}
                        required
                    />
                    <label style={styles.label}>Client phone & access</label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                        <input
                            style={styles.input}
                            value={clientPhone}
                            onChange={e => setClientPhone(e.target.value)}
                            placeholder="Phone (e.g. 9045551234)"
                        />
                        <input
                            style={styles.input}
                            value={clientPhone2}
                            onChange={e => setClientPhone2(e.target.value)}
                            placeholder="2nd phone (optional)"
                        />
                        <input
                            style={styles.input}
                            value={customPassword}
                            onChange={e => setCustomPassword(e.target.value)}
                            placeholder="Custom password (if no phone)"
                        />
                    </div>
                    <label style={styles.label}>Intro message (shown above the projects)</label>
                    <textarea
                        style={{ ...styles.input, minHeight: '100px' }}
                        value={introMessage}
                        onChange={e => setIntroMessage(e.target.value)}
                    />
                </section>

                {projects.map((project, pi) => (
                    <section key={pi} style={styles.section}>
                        <div style={styles.projectHeader}>
                            <h2 style={styles.sectionTitle}>Project {pi + 1}</h2>
                            {projects.length > 1 && (
                                <button type="button" onClick={() => removeProject(pi)} style={styles.removeBtn}>
                                    Remove
                                </button>
                            )}
                        </div>

                        <label style={styles.label}>Title</label>
                        <input
                            style={styles.input}
                            value={project.title}
                            onChange={e => updateProject(pi, 'title', e.target.value)}
                            placeholder="e.g. Project 3: Drywall & Wall Repairs"
                            required
                        />

                        <label style={styles.label}>Project description</label>
                        <textarea
                            style={{ ...styles.input, minHeight: '80px' }}
                            value={project.description}
                            onChange={e => updateProject(pi, 'description', e.target.value)}
                        />

                        {/* Option comparison section */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.25rem' }}>
                            <label style={styles.label}>Option comparison cards</label>
                            <button
                                type="button"
                                style={styles.addBtn}
                                onClick={() => {
                                    setProjects(prev => {
                                        const updated = [...prev];
                                        updated[pi] = {
                                            ...updated[pi],
                                            optionCards: [...updated[pi].optionCards, { title: '', url: '', comment: '', bullets: [''] }]
                                        };
                                        return updated;
                                    });
                                }}
                            >
                                + Add option card
                            </button>
                        </div>

                        {project.optionCards.map((card, ci) => (
                            <div key={ci} style={styles.groupCard}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>Option {ci + 1}</span>
                                    <button type="button" onClick={() => {
                                        setProjects(prev => {
                                            const updated = [...prev];
                                            updated[pi] = {
                                                ...updated[pi],
                                                optionCards: updated[pi].optionCards.filter((_, i) => i !== ci)
                                            };
                                            return updated;
                                        });
                                    }} style={styles.removeBtn}>Remove</button>
                                </div>

                                <label style={styles.label}>Title</label>
                                <input
                                    style={styles.input}
                                    value={card.title}
                                    placeholder="e.g. Porcelain tile"
                                    onChange={e => {
                                        setProjects(prev => {
                                            const updated = [...prev];
                                            const cards = [...updated[pi].optionCards];
                                            cards[ci] = { ...cards[ci], title: e.target.value };
                                            updated[pi] = { ...updated[pi], optionCards: cards };
                                            return updated;
                                        });
                                    }}
                                />

                                <label style={styles.label}>URL (optional — makes title a link)</label>
                                <input
                                    style={styles.input}
                                    value={card.url}
                                    placeholder="e.g. https://homedepot.com/..."
                                    onChange={e => {
                                        setProjects(prev => {
                                            const updated = [...prev];
                                            const cards = [...updated[pi].optionCards];
                                            cards[ci] = { ...cards[ci], url: e.target.value };
                                            updated[pi] = { ...updated[pi], optionCards: cards };
                                            return updated;
                                        });
                                    }}
                                />

                                <label style={styles.label}>Description (optional)</label>
                                <textarea
                                    style={{ ...styles.input, minHeight: '70px' }}
                                    value={card.comment}
                                    placeholder="e.g. Horizontal nickel-gap pine boards..."
                                    onChange={e => {
                                        setProjects(prev => {
                                            const updated = [...prev];
                                            const cards = [...updated[pi].optionCards];
                                            cards[ci] = { ...cards[ci], comment: e.target.value };
                                            updated[pi] = { ...updated[pi], optionCards: cards };
                                            return updated;
                                        });
                                    }}
                                />

                                <label style={styles.label}>Bullet points (optional)</label>
                                {card.bullets.map((bullet, bi) => (
                                    <div key={bi} style={styles.listRow}>
                                        <input
                                            style={{ ...styles.input, flex: 1, marginBottom: 0 }}
                                            value={bullet}
                                            placeholder="e.g. Durability: Extremely durable and water resistant"
                                            onChange={e => {
                                                setProjects(prev => {
                                                    const updated = [...prev];
                                                    const cards = [...updated[pi].optionCards];
                                                    const bullets = [...cards[ci].bullets];
                                                    bullets[bi] = e.target.value;
                                                    cards[ci] = { ...cards[ci], bullets };
                                                    updated[pi] = { ...updated[pi], optionCards: cards };
                                                    return updated;
                                                });
                                            }}
                                        />
                                        {card.bullets.length > 1 && (
                                            <button type="button" style={styles.removeSmall} onClick={() => {
                                                setProjects(prev => {
                                                    const updated = [...prev];
                                                    const cards = [...updated[pi].optionCards];
                                                    const bullets = cards[ci].bullets.filter((_, i) => i !== bi);
                                                    cards[ci] = { ...cards[ci], bullets };
                                                    updated[pi] = { ...updated[pi], optionCards: cards };
                                                    return updated;
                                                });
                                            }}>✕</button>
                                        )}
                                    </div>
                                ))}
                                <button type="button" style={styles.addBtn} onClick={() => {
                                    setProjects(prev => {
                                        const updated = [...prev];
                                        const cards = [...updated[pi].optionCards];
                                        cards[ci] = { ...cards[ci], bullets: [...cards[ci].bullets, ''] };
                                        updated[pi] = { ...updated[pi], optionCards: cards };
                                        return updated;
                                    });
                                }}>+ Add bullet</button>
                            </div>
                        ))}

                        {project.optionCards.length > 0 && (
                            <>
                                <label style={styles.label}>Option comparison comment (optional)</label>
                                <textarea
                                    style={{ ...styles.input, minHeight: '70px' }}
                                    value={project.optionCardsComment}
                                    onChange={e => updateProject(pi, 'optionCardsComment', e.target.value)}
                                    placeholder="e.g. I'd go with the shiplap. The island is surrounded by..."
                                />
                            </>
                        )}

                        <label style={styles.label}>Scope of work items</label>
                        {project.scopeItems.map((item, si) => (
                            <div key={si} style={styles.listRow}>
                                <input
                                    style={{ ...styles.input, flex: 1, marginBottom: 0 }}
                                    value={item}
                                    onChange={e => updateListItem(pi, 'scopeItems', si, e.target.value)}
                                    placeholder="e.g. Corner bead: Replace all damaged sections..."
                                />
                                {project.scopeItems.length > 1 && (
                                    <button type="button" onClick={() => removeListItem(pi, 'scopeItems', si)} style={styles.removeSmall}>✕</button>
                                )}
                            </div>
                        ))}
                        <button type="button" onClick={() => addListItem(pi, 'scopeItems')} style={styles.addBtn}>+ Add scope item</button>

                        <label style={styles.label}>Time expectation</label>
                        <input
                            style={styles.input}
                            value={project.timeExpectation}
                            onChange={e => updateProject(pi, 'timeExpectation', e.target.value)}
                            placeholder="e.g. 9-10 hours. No overnight required."
                        />

                        <label style={styles.label}>Scope comment (optional)</label>
                        <textarea
                            style={{ ...styles.input, minHeight: '70px' }}
                            value={project.scopeComment}
                            onChange={e => updateProject(pi, 'scopeComment', e.target.value)}
                            placeholder="e.g. All work will be done in a single visit..."
                        />

                        {/* Materials section */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.75rem' }}>
                            <label style={styles.label}>Materials</label>
                            <button
                                type="button"
                                style={styles.addBtn}
                                onClick={() => updateProject(pi, 'materialsMode', project.materialsMode === 'single' ? 'multi' : 'single')}
                            >
                                {project.materialsMode === 'single' ? 'Switch to option cards' : 'Switch to flat list'}
                            </button>
                        </div>

                        {project.materialsMode === 'single' ? (
                            <>
                                {project.materials.map((item, mi) => (
                                    <div key={mi} style={styles.listRow}>
                                        <input
                                            style={{ ...styles.input, flex: 1, marginBottom: 0 }}
                                            value={item}
                                            onChange={e => updateListItem(pi, 'materials', mi, e.target.value)}
                                            placeholder="e.g. Joint compound: ~$15"
                                        />
                                        {project.materials.length > 1 && (
                                            <button type="button" onClick={() => removeListItem(pi, 'materials', mi)} style={styles.removeSmall}>✕</button>
                                        )}
                                    </div>
                                ))}
                                <button type="button" onClick={() => addListItem(pi, 'materials')} style={styles.addBtn}>+ Add material</button>
                            </>
                        ) : (
                            <>
                                {project.materialGroups.map((group, gi) => (
                                    <div key={gi} style={styles.groupCard}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>Option {gi + 1}</span>
                                            {project.materialGroups.length > 1 && (
                                                <button type="button" onClick={() => {
                                                    setProjects(prev => {
                                                        const updated = [...prev];
                                                        updated[pi] = {
                                                            ...updated[pi],
                                                            materialGroups: updated[pi].materialGroups.filter((_, i) => i !== gi)
                                                        };
                                                        return updated;
                                                    });
                                                }} style={styles.removeBtn}>Remove</button>
                                            )}
                                        </div>
                                        <label style={styles.label}>Title</label>
                                        <input
                                            style={styles.input}
                                            value={group.title}
                                            placeholder="e.g. Option A: Reuse existing wire shelf"
                                            onChange={e => {
                                                setProjects(prev => {
                                                    const updated = [...prev];
                                                    const groups = [...updated[pi].materialGroups];
                                                    groups[gi] = { ...groups[gi], title: e.target.value };
                                                    updated[pi] = { ...updated[pi], materialGroups: groups };
                                                    return updated;
                                                });
                                            }}
                                        />
                                        <label style={styles.label}>Description</label>
                                        <textarea
                                            style={{ ...styles.input, minHeight: '70px' }}
                                            value={group.comment}
                                            placeholder="e.g. The existing shelf gets relocated..."
                                            onChange={e => {
                                                setProjects(prev => {
                                                    const updated = [...prev];
                                                    const groups = [...updated[pi].materialGroups];
                                                    groups[gi] = { ...groups[gi], comment: e.target.value };
                                                    updated[pi] = { ...updated[pi], materialGroups: groups };
                                                    return updated;
                                                });
                                            }}
                                        />
                                        <label style={styles.label}>Cost line</label>
                                        <input
                                            style={styles.input}
                                            value={group.cost}
                                            placeholder="e.g. Material cost: ~$45"
                                            onChange={e => {
                                                setProjects(prev => {
                                                    const updated = [...prev];
                                                    const groups = [...updated[pi].materialGroups];
                                                    groups[gi] = { ...groups[gi], cost: e.target.value };
                                                    updated[pi] = { ...updated[pi], materialGroups: groups };
                                                    return updated;
                                                });
                                            }}
                                        />
                                        <label style={styles.label}>URL (optional — makes cost line a link)</label>
                                        <input
                                            style={styles.input}
                                            value={group.url || ''}
                                            placeholder="e.g. https://www.homedepot.com/..."
                                            onChange={e => {
                                                setProjects(prev => {
                                                    const updated = [...prev];
                                                    const groups = [...updated[pi].materialGroups];
                                                    groups[gi] = { ...groups[gi], url: e.target.value };
                                                    updated[pi] = { ...updated[pi], materialGroups: groups };
                                                    return updated;
                                                });
                                            }}
                                        />
                                    </div>
                                ))}
                                <button type="button" style={styles.addBtn} onClick={() => {
                                    setProjects(prev => {
                                        const updated = [...prev];
                                        updated[pi] = {
                                            ...updated[pi],
                                            materialGroups: [...updated[pi].materialGroups, { title: '', comment: '', cost: '', url: '' }]
                                        };
                                        return updated;
                                    });
                                }}>+ Add option</button>
                            </>
                        )}

                        <label style={styles.label}>Materials comment (optional)</label>
                        <textarea
                            style={{ ...styles.input, minHeight: '70px' }}
                            value={project.materialsComment}
                            onChange={e => updateProject(pi, 'materialsComment', e.target.value)}
                            placeholder="e.g. If there's any chance the other walls get updated..."
                        />

                        <div style={styles.pricingGrid}>
                            <div>
                                <label style={styles.label}>Labor (flat rate)</label>
                                <input
                                    style={styles.input}
                                    value={project.laborPrice}
                                    onChange={e => updateProject(pi, 'laborPrice', e.target.value)}
                                    placeholder="e.g. $550"
                                />
                            </div>
                            <div>
                                <label style={styles.label}>Materials estimate</label>
                                <input
                                    style={styles.input}
                                    value={project.materialsEstimate}
                                    onChange={e => updateProject(pi, 'materialsEstimate', e.target.value)}
                                    placeholder="e.g. ~$105"
                                />
                            </div>
                            <div>
                                <label style={styles.label}>Estimated total</label>
                                <input
                                    style={styles.input}
                                    value={project.totalPrice}
                                    onChange={e => updateProject(pi, 'totalPrice', e.target.value)}
                                    placeholder="e.g. $655"
                                />
                            </div>
                        </div>

                        <label style={styles.label}>Pricing note (optional)</label>
                        <input
                            style={styles.input}
                            value={project.pricingNote}
                            onChange={e => updateProject(pi, 'pricingNote', e.target.value)}
                            placeholder="e.g. Materials billed at exact cost..."
                        />
                    </section>
                ))}

                <button type="button" onClick={addProject} style={styles.addProjectBtn}>+ Add project</button>

                {error && <p style={styles.errorText}>{error}</p>}

                <button type="submit" disabled={submitting} style={styles.submitBtn}>
                    {submitting ? 'Creating...' : 'Create quote'}
                </button>
            </form>
        </div>
    );
}

const styles = {
    groupCard: {
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        padding: '1rem',
        marginBottom: '0.75rem',
        display: 'flex',
        flexDirection: 'column',
        background: '#f9fafb',
    },
    gate: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f9fafb',
    },
    gateBox: {
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '2rem',
        width: '100%',
        maxWidth: '400px',
        textAlign: 'center',
    },
    gateTitle: { marginBottom: '0.25rem' },
    gateSubtitle: { color: '#6b7280', marginBottom: '1.5rem' },
    page: { maxWidth: '720px', margin: '0 auto', padding: '2rem 1rem 6rem' },
    pageTitle: { marginBottom: '1.5rem' },
    form: { display: 'flex', flexDirection: 'column', gap: '0' },
    section: {
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '1.5rem',
        marginBottom: '1.5rem',
        display: 'flex',
        flexDirection: 'column',
    },
    sectionTitle: { marginTop: 0, marginBottom: '1rem' },
    projectHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    label: { fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.25rem', marginTop: '0.75rem' },
    input: {
        padding: '0.5rem 0.75rem',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        fontSize: '0.95rem',
        marginBottom: '0.25rem',
        width: '100%',
        boxSizing: 'border-box',
        fontFamily: 'inherit',
    },
    listRow: { display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.4rem' },
    addBtn: {
        alignSelf: 'flex-start',
        background: 'none',
        border: 'none',
        color: '#2563eb',
        cursor: 'pointer',
        padding: '0.25rem 0',
        fontSize: '0.875rem',
        marginTop: '0.25rem',
    },
    removeBtn: {
        background: 'none',
        border: 'none',
        color: '#ef4444',
        cursor: 'pointer',
        fontSize: '0.875rem',
    },
    removeSmall: {
        background: 'none',
        border: 'none',
        color: '#9ca3af',
        cursor: 'pointer',
        fontSize: '1rem',
        padding: '0 0.25rem',
        flexShrink: 0,
    },
    pricingGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '1rem',
        marginTop: '0.5rem',
    },
    addProjectBtn: {
        background: '#f3f4f6',
        border: '1px dashed #9ca3af',
        borderRadius: '8px',
        padding: '0.75rem',
        cursor: 'pointer',
        fontSize: '0.95rem',
        marginBottom: '1.5rem',
    },
    submitBtn: {
        background: '#111827',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        padding: '0.85rem',
        fontSize: '1rem',
        cursor: 'pointer',
        fontWeight: 600,
    },
    errorText: { color: '#ef4444', fontSize: '0.875rem' },
    link: { color: '#2563eb', wordBreak: 'break-all' },
    button: {
        background: '#111827',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        padding: '0.65rem 1.25rem',
        fontSize: '0.95rem',
        cursor: 'pointer',
        width: '100%',
        marginTop: '0.5rem',
    },
};

export default NewQuote;